import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { MapPin, Users, Heart, Star, Wifi, DollarSign, Bus, Smartphone, Search, AlertTriangle, Utensils, Shield, Globe, ExternalLink, Clock, Coffee, Zap, Camera, Map, MessageCircle, TrendingUp, Award, Phone, Bookmark, BookmarkCheck, Lightbulb, Share2, Plus, X, ChevronRight, Sparkles, ChevronDown, RefreshCw, Calendar, ThermometerSun, Cloud, Copy, Volume2, ThumbsUp, Filter, AlertCircle, Info, Download, Lock, Crown } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LocationAutocomplete } from '@/components/ui/location-autocomplete';
import { RecentSearches } from '@/components/ui/recent-searches';
import { ESIMRecommendations } from './ESIMRecommendations';
import { useToast } from '@/hooks/use-toast';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { useBookmarks } from '@/hooks/useBookmarks';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useUserPreferences, TravelStyle, TripType, TravelerType } from '@/hooks/useUserPreferences';
import { generateTravelTips } from '@/utils/dynamicTravelTips';
import { supabase } from '@/integrations/supabase/client';
import jsPDF from 'jspdf';

interface LocalTip {
  id: string;
  category: 'transport' | 'connectivity' | 'culture' | 'costs' | 'safety' | 'etiquette' | 'food' | 'attractions' | 'weather' | 'language' | 'emergency';
  title: string;
  content: string;
  author: string;
  rating: number;
  likes: number;
  icon: React.ReactNode;
  estimatedCost?: string;
  lastUpdated: Date;
  verified: boolean;
  priority: 'high' | 'medium' | 'low';
  helpful?: number;
  isBookmarked?: boolean;
}

interface RealTimeData {
  weather?: {
    temperature: number;
    condition: string;
    humidity: number;
    lastUpdated: Date;
  };
  exchangeRate?: {
    rate: number;
    currency: string;
    lastUpdated: Date;
  };
  currentEvents?: Array<{
    title: string;
    description: string;
    impact: 'high' | 'medium' | 'low';
    category: string;
  }>;
}

interface QuickTip {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  actionable: boolean;
}

interface InsightCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  priority: number;
}

interface FavoriteDestination {
  name: string;
  country: string;
  addedAt: Date;
}

interface DidYouKnow {
  fact: string;
  category: string;
}

interface LocalTipsProps {
  destination?: string;
  destinationCountry?: string;
}

interface TipSection {
  title: string;
  icon: React.ReactNode;
  tips: LocalTip[];
  realTimeData?: RealTimeData;
}

// Dynamic tips generation - now supports all 2000+ destinations

// Popular destinations with country flags
const POPULAR_DESTINATIONS = [
  { name: 'Paris', country: 'France', flag: '🇫🇷' },
  { name: 'Tokyo', country: 'Japan', flag: '🇯🇵' },
  { name: 'New York', country: 'USA', flag: '🇺🇸' },
  { name: 'London', country: 'UK', flag: '🇬🇧' },
  { name: 'Dubai', country: 'UAE', flag: '🇦🇪' },
  { name: 'Barcelona', country: 'Spain', flag: '🇪🇸' },
  { name: 'Rome', country: 'Italy', flag: '🇮🇹' },
  { name: 'Sydney', country: 'Australia', flag: '🇦🇺' },
  { name: 'Amsterdam', country: 'Netherlands', flag: '🇳🇱' },
  { name: 'Singapore', country: 'Singapore', flag: '🇸🇬' },
  { name: 'Bangkok', country: 'Thailand', flag: '🇹🇭' },
  { name: 'Istanbul', country: 'Turkey', flag: '🇹🇷' },
];


const getTipsForDestination = (destination: string, country: string) => {
  return generateTravelTips(destination, country);
};
export function LocalTips({ destination, destinationCountry }: LocalTipsProps) {
  const [tips, setTips] = useState<LocalTip[]>([]);
  const [loading, setLoading] = useState(false);
  const [userDestination, setUserDestination] = useState(destination || '');
  const [hasSearched, setHasSearched] = useState(false);
  
  // SEO data
  const pageTitle = userDestination 
    ? `${userDestination} Travel Tips & Local Insights | OdyVoy`
    : 'Local Travel Tips & Cultural Insights | OdyVoy';
  const pageDescription = userDestination
    ? `Discover essential travel tips, local customs, safety advice, and cultural insights for ${userDestination}, ${destinationCountry || ''}. Get real-time information from locals and experienced travelers.`
    : 'Access comprehensive travel guides with local tips, cultural insights, safety advice, and practical information for destinations worldwide. Real-time updates from travelers and locals.';
  
  // Quick Travel Insights state
  const [selectedInsight, setSelectedInsight] = useState<string | null>(null);
  const [insightDestination, setInsightDestination] = useState('');
  const [insightCountry, setInsightCountry] = useState('');
  const [insightLoading, setInsightLoading] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteDestination[]>([]);
  const [showUserTipForm, setShowUserTipForm] = useState(false);
  const [userTip, setUserTip] = useState({ title: '', content: '', category: 'culture' });
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  
  const { addRecentSearch } = useRecentSearches();
  const { addBookmark, removeBookmark, isBookmarked, getBookmarkId } = useBookmarks();
  const { user } = useAuth();
  const { subscription, hasFeature } = useSubscription();
  
  // Check if user has access to local tips features (premium_economy or first_class)
  const canUseLocalTips = hasFeature('language_phrases');

  // Personalization
  const { preferences, updatePreferences, loading: preferencesLoading } = useUserPreferences();
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  // UI state
  const [showPopularDestinations, setShowPopularDestinations] = useState(false);

  // Real-time data management
  const [realTimeData, setRealTimeData] = useState<RealTimeData>({});
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [dataAccuracy, setDataAccuracy] = useState<'excellent' | 'good' | 'fair'>('good');
  const { toast } = useToast();

  // Live data states for Perplexity API
  const [liveData, setLiveData] = useState<any>(null);
  const [loadingLiveData, setLoadingLiveData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Personalization filters
  const [budgetFilter, setBudgetFilter] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Helpful tips tracking
  const [helpfulTips, setHelpfulTips] = useState<Set<string>>(new Set());
  
  // Cache management - extended duration for faster loads
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  const CACHE_KEY_PREFIX = 'local_tips_cache_';

  // Load helpful tips from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('helpful_tips');
    if (saved) {
      setHelpfulTips(new Set(JSON.parse(saved)));
    }
  }, []);

  // Cache management functions
  const getCachedData = (destination: string) => {
    const cacheKey = `${CACHE_KEY_PREFIX}${destination.toLowerCase()}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return data;
  };

  const setCachedData = (destination: string, data: any) => {
    const cacheKey = `${CACHE_KEY_PREFIX}${destination.toLowerCase()}`;
    localStorage.setItem(cacheKey, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  };

  // Copy to clipboard function
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
      });
    }).catch(() => {
      toast({
        title: "Copy failed",
        description: "Please try again",
        variant: "destructive"
      });
    });
  };

  // Make phone call (works on mobile)
  const makePhoneCall = (number: string) => {
    window.location.href = `tel:${number}`;
  };

  // Text-to-speech for pronunciation
  const pronounceText = (text: string, lang: string = 'en-US') => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      speechSynthesis.speak(utterance);
      toast({
        title: "Playing pronunciation",
        description: text,
      });
    } else {
      toast({
        title: "Not supported",
        description: "Text-to-speech is not available on this device",
        variant: "destructive"
      });
    }
  };

  // Mark tip as helpful
  const markAsHelpful = (tipId: string) => {
    const newHelpful = new Set(helpfulTips);
    if (newHelpful.has(tipId)) {
      newHelpful.delete(tipId);
    } else {
      newHelpful.add(tipId);
    }
    setHelpfulTips(newHelpful);
    localStorage.setItem('helpful_tips', JSON.stringify(Array.from(newHelpful)));
    
    toast({
      title: newHelpful.has(tipId) ? "Marked as helpful" : "Removed from helpful",
      description: "Your feedback helps improve recommendations",
    });
  };

  // Note: live weather/events are sourced from the destination-insights function,
  // not simulated locally. No placeholder data is generated here.

  const handleRefresh = async () => {
    if (!insightDestination) return;
    
    setRefreshing(true);
    
    // Clear cache for this destination
    const cacheKey = `${CACHE_KEY_PREFIX}${insightDestination.toLowerCase()}`;
    localStorage.removeItem(cacheKey);
    
    // Refetch data
    await fetchLiveInsights();
    
    setRefreshing(false);
    setLastRefreshed(new Date());
    
    toast({
      title: "Data Updated",
      description: "Local tips refreshed with latest information",
    });
  };

  const handleDownloadPDF = () => {
    if (!liveData || !insightDestination) return;
    
    // Check subscription tier
    if (subscription.subscription_tier !== 'first_class') {
      toast({
        title: "Premium Feature",
        description: "Offline access is available for First Class subscribers only.",
        variant: "destructive"
      });
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      const lineHeight = 7;
      let yPosition = 20;

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(`${insightDestination} Travel Guide`, margin, yPosition);
      yPosition += lineHeight * 2;

      // Subtitle
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, margin, yPosition);
      yPosition += lineHeight * 2;

      // Categories
      const categories = [
        { title: 'Safety & Emergency Info', content: liveData.safety },
        { title: 'Transport & Getting Around', content: liveData.transport },
        { title: 'Local Etiquette & Culture', content: liveData.etiquette },
        { title: 'Food Culture & Dining', content: liveData.food }
      ];

      categories.forEach((category) => {
        // Check if we need a new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }

        // Category title
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(category.title, margin, yPosition);
        yPosition += lineHeight * 1.5;

        // Category content
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        const lines = doc.splitTextToSize(category.content, pageWidth - (margin * 2));
        lines.forEach((line: string) => {
          if (yPosition > 280) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, margin, yPosition);
          yPosition += lineHeight * 0.8;
        });
        
        yPosition += lineHeight;
      });

      // Save the PDF
      doc.save(`${insightDestination.replace(/\s/g, '_')}_travel_guide.pdf`);
      
      // Cache destination for offline access
      const offlineKey = `offline_destination_${insightDestination.toLowerCase()}`;
      localStorage.setItem(offlineKey, JSON.stringify({
        destination: insightDestination,
        country: insightCountry,
        data: liveData,
        savedAt: new Date().toISOString()
      }));

      toast({
        title: "Download Complete",
        description: `${insightDestination} travel guide saved for offline access`,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Download Failed",
        description: "Unable to generate PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'transport': return <Bus className="h-4 w-4" />;
      case 'connectivity': return <Smartphone className="h-4 w-4" />;
      case 'culture': return <Users className="h-4 w-4" />;
      case 'costs': return <DollarSign className="h-4 w-4" />;
      case 'safety': return <Shield className="h-4 w-4" />;
      case 'etiquette': return <Utensils className="h-4 w-4" />;
      case 'food': return <Coffee className="h-4 w-4" />;
      case 'attractions': return <Camera className="h-4 w-4" />;
      case 'weather': return <Zap className="h-4 w-4" />;
      case 'language': return <MessageCircle className="h-4 w-4" />;
      case 'emergency': return <Phone className="h-4 w-4" />;
      default: return <MapPin className="h-4 w-4" />;
    }
  };

  const handleSearch = () => {
    if (userDestination.trim()) {
      setHasSearched(true);
      setLoading(true);
      setTimeout(() => {
        setTips(getMockTips(userDestination));
        setLoading(false);
      }, 800);
    }
  };

  const organizeTipsBySections = (tips: LocalTip[]): TipSection[] => {
    return [
      {
        title: 'Etiquette & Cultural Norms',
        icon: <Utensils className="h-5 w-5" />,
        tips: tips.filter(tip => tip.category === 'etiquette' || tip.category === 'culture')
      },
      {
        title: 'Local Transportation Options',
        icon: <Bus className="h-5 w-5" />,
        tips: tips.filter(tip => tip.category === 'transport')
      },
      {
        title: 'Food & Dining Experience',
        icon: <Coffee className="h-5 w-5" />,
        tips: tips.filter(tip => tip.category === 'food')
      },
      {
        title: 'Must-Visit Attractions',
        icon: <Camera className="h-5 w-5" />,
        tips: tips.filter(tip => tip.category === 'attractions')
      },
      {
        title: 'Important Travel Information',
        icon: <AlertTriangle className="h-5 w-5" />,
        tips: tips.filter(tip => tip.category === 'safety' || tip.category === 'costs' || tip.category === 'connectivity' || tip.category === 'emergency' || tip.category === 'weather')
      }
    ];
  };

  const getQuickTips = (destination: string): QuickTip[] => {
    return [
      {
        id: '1',
        icon: <Clock className="h-5 w-5" />,
        title: 'Best Time to Visit',
        description: 'Optimal weather and fewer crowds',
        actionable: true
      },
      {
        id: '2', 
        icon: <DollarSign className="h-5 w-5" />,
        title: 'Budget Estimate',
        description: 'Daily costs for travelers',
        actionable: true
      },
      {
        id: '3',
        icon: <MessageCircle className="h-5 w-5" />,
        title: 'Key Phrases',
        description: 'Essential local expressions',
        actionable: true
      },
      {
        id: '4',
        icon: <Phone className="h-5 w-5" />,
        title: 'Emergency Contacts',
        description: 'Important numbers to know',
        actionable: true
      }
    ];
  };

  // Enhanced destination-specific mock data
  const getMockTips = (destination?: string): LocalTip[] => {
    const baseDestination = destination?.toLowerCase() || '';
    
    // Create destination-specific content
    const getDestinationSpecificContent = () => {
      const destinationMap: Record<string, {transport: string, etiquette: string, safety: string, food: string}> = {
        // European Major Cities
        'paris': {
          transport: `Download Citymapper, Moovit apps. Buy Navigo Semaine pass (€32.40/week, all zones, valid Mon-Sun). Apps: G7 Taxi, Uber, Bolt. Vélib' bike sharing citywide. Avoid rush 8-9am, 6-7pm.`,
          etiquette: `Always greet "Bonjour/Bonsoir". Learn basic French. Dress elegantly. Don't eat while walking. Wait for "Bon appétit".`,
          safety: `Generally safe but watch pickpockets on Metro. Emergency: Police 17, Medical 15, Fire 18, EU Emergency 112. Tourist police speak English.`,
          food: `Breakfast: croissants, café au lait. Lunch 12-2pm (long breaks). Dinner 7-9pm. Try bistros, brasseries. Wine with meals normal. Bread free. Service charge included but round up. Don't rush meals - dining is social.`
        },
        'london': {
          transport: `Download TfL Go app. Use Oyster Card/contactless. Apps: Uber, Bolt, Free Now. Santander Cycles 24/7. Daily contactless fare cap applies. Night Tube Fri/Sat.`,
          etiquette: `Queue properly - sacred in London. "Please/thank you/sorry" constantly. Mind the gap! Stand right on escalators. Tip 10-15%.`,
          safety: `Very safe but alert in tourist areas. Emergency: 999 or 112. Police wear high-vis jackets. Non-emergency: 101.`,
          food: `Full English breakfast traditional. Pub culture important - gastropubs elevated food. Afternoon tea 3-5pm. Fish & chips classic. Sunday roast popular. Tipping 10-15% restaurants, pubs no tipping.`
        },
        'rome': {
          transport: `Download Citymapper Rome, Moovit. Buy Roma Pass. Apps: Free Now, Uber, ItTaxi. Always validate tickets - fine is €54.90 if paid within 5 days, €104.90 after.`,
          etiquette: `Greet "Buongiorno/Buonasera". Dress modestly in churches. Dinner 8-10pm. No cappuccino after meals. Round up tips.`,
          safety: `Safe but watch pickpockets near attractions. Emergency: Police 113, Medical 118, Fire 115, EU 112. Tourist police multilingual.`,
          food: `Breakfast: espresso and cornetto. Lunch 1-3pm. Aperitivo 6-8pm. Late dinner 8-10pm. Carbonara, cacio e pepe classics. No cheese with seafood pasta. Gelato year-round. Trattoria vs ristorante prices.`
        },
        'madrid': {
          transport: `Download EMT Madrid, Metro apps. Tourist Travel Pass available. Apps: Uber, Cabify, Free Now. BiciMAD bike sharing.`,
          etiquette: `Greet "Hola, Buenos días/tardes". Very late dinner (9-11pm). Siesta 2-5pm. Cheek kisses. Tip 5-10%.`,
          safety: `Very safe, low crime. Emergency: Police 091, Medical 061, Fire 080, EU 112. Tourist police speak English.`,
          food: `Breakfast: café con leche, tostada. Tapas culture - small plates shared. Late lunch 2-4pm. Very late dinner 9-11pm. Jamón ibérico premium. Churros con chocolate dessert. Mercado San Miguel food market.`
        },
        'barcelona': {
          transport: `Download TMB App. T-casual card (10 single trips, replaced the old T-10) or Hola Barcelona travel card for tourists. Apps: Uber, Cabify, Free Now. Bicing needs local registration.`,
          etiquette: `Learn Catalan "Bon dia". Catalans proud of culture. Late dinner (9-11pm). Quiet in public. Tip 5-10%.`,
          safety: `Safe but pickpocketing common in tourist areas. Emergency: Police 091, Mossos 088, Medical 061, EU 112.`,
          food: `Breakfast: café amb llet, pa amb tomàquet. Tapas culture strong. Late lunch 2-4pm, dinner 9-11pm. Paella authentic from Valencia. Cava sparkling wine. Pintxos bars. La Boquería market famous.`
        },
        'berlin': {
          transport: `Download BVG app. WelcomeCard for unlimited transport. Apps: Uber, Free Now, Free Now (formerly MyTaxi). Nextbike cycling.`,
          etiquette: `Germans value punctuality, directness. Separate recycling serious. Don't jaywalk. Quiet hours 10pm-6am. Tip 10%.`,
          safety: `Very safe with efficient police. Emergency: Police 110, Fire/Medical 112. Tourist police in major areas.`,
          food: `Breakfast: brötchen (rolls), coffee. Currywurst Berlin specialty. Beer culture strong - beer gardens popular. Dinner 6-8pm earlier than south. Döner kebab popular fast food. Cash preferred many places.`
        },
        'amsterdam': {
          transport: `Download GVB app. Rent bikes - 400km paths. Apps: Uber, Bolt, Staxi. Watch trams - right of way. Canal boats.`,
          etiquette: `Dutch direct but friendly. Excellent English. "Going Dutch" - split bills. Don't block bike paths!`,
          safety: `Very safe but watch bikes/mopeds. Emergency: 112. Red light district tourist-friendly but secure belongings.`,
          food: `Breakfast: bread with hagelslag (sprinkles). Stroopwafel traditional. Cheese culture - try aged gouda. Herring raw popular snack. Brown cafés (brown bars) for local atmosphere. Dinner 6-8pm.`
        },
        'vienna': {
          transport: `Download WienMobil app. Vienna Card includes transport/attractions. Apps: Uber, Bolt, 40100 Taxi. CityBike Wien citywide.`,
          etiquette: `Austrians formal - use titles/surnames initially. Punctuality crucial. Quiet on transport. Tip 10%. Remove shoes homes.`,
          safety: `One of world's safest cities. Emergency: Police 133, Fire 122, Medical 144, EU 112. Tourist police near attractions.`,
          food: `Coffee house culture UNESCO protected. Sachertorte famous. Schnitzel traditional - not with sauce. Apple strudel dessert. Heuriger wine taverns. Breakfast: semmel with butter, jam.`
        },
        'prague': {
          transport: `Download PID Lítačka app. Prague Castle ticket combo with transport. Apps: Uber, Bolt, Liftago. Bike sharing limited old town.`,
          etiquette: `Czechs reserved but warm once you know them. Learn "Dobrý den". Don't expect smiles service staff. Tip 10% rounding up.`,
          safety: `Very safe, low crime rates. Emergency: Police 158, Fire 150, Medical 155, EU 112. Tourist police speak English.`,
          food: `Breakfast: rohlík (roll) with butter. Goulash traditional. Beer culture exceptional - pilsner originated here. Svíčková national dish. Knedlíky (dumplings) side dish. Heavy, hearty meals.`
        },
        'budapest': {
          transport: `Download BudapestGO app. Budapest Card for transport/discounts. Apps: Uber, Bolt, Főtaxi. MOL Bubi bike sharing.`,
          etiquette: `Hungarians formal initially - handshakes important. Learn "Jó napot". Thermal baths have etiquette rules. Tip 10-15%.`,
          safety: `Generally safe but cautious at night in party districts. Emergency: Police 107, Fire 105, Medical 104, EU 112.`,
          food: `Breakfast: kifli (croissant) with coffee. Goulash (gulyás) signature dish. Paprika widely used. Langos fried bread snack. Palinka fruit brandy strong. Thermal bath complexes often have restaurants.`
        },
        'lisbon': {
          transport: `Download Moovit, Citymapper. Lisboa Card transport/attractions. Apps: Uber, Bolt, Mytaxi. GIRA bike sharing.`,
          etiquette: `Portuguese friendly, welcoming. Learn "Olá", "Obrigado/a". Lunch 12-2pm, dinner 7-9pm. Tip 5-10%. Dress modestly churches.`,
          safety: `Very safe, friendly locals. Emergency: Police 112, PSP 21 361 2000. Tourist police speak multiple languages.`,
          food: `Breakfast: café with pastel de nata (custard tart). Seafood excellent - grilled sardines popular. Bacalhau (cod) national dish. Port wine from Porto region. Late lunch 1-3pm, dinner 7-9pm.`
        },
        'athens': {
          transport: `Download OASA Telematics app. 3-day tourist tickets unlimited rides. Apps: Uber, Beat, official taxis. Metro efficient major sites.`,
          etiquette: `Greeks hospitable, social. Learn "Yassas". Lunch late (2-3pm), dinner very late (9-11pm). Tip 10% standard.`,
          safety: `Generally safe but watch pickpockets tourist areas. Emergency: Police 100, Tourist Police 171, Medical 166, EU 112.`,
          food: `Breakfast: Greek coffee, koulouria (bread rings). Mezze culture - small shared plates. Moussaka classic. Greek salad (horiatiki) no lettuce. Ouzo anise-flavored aperitif. Souvlaki popular street food.`
        },
        'florence': {
          transport: `Download Moovit, ATAF app buses. Historic center best on foot. Apps: Uber, Free Now, official taxis. Limited bike lanes.`,
          etiquette: `Florentines take art/history seriously. Dress code churches - cover shoulders/knees. Aperitivo 6-8pm important. Round up tips.`,
          safety: `Very safe, low crime rates. Emergency: Police 113, Carabinieri 112, Medical 118, Fire 115, EU 112.`,
          food: `Breakfast: cappuccino with cornetto. Florentine steak (bistecca alla fiorentina) famous. Ribollita soup traditional. Aperitivo 6-8pm before dinner. Gelato artisanal. Chianti wine region nearby.`
        },
        'venice': {
          transport: `Download ACTV app for vaporetto (water buses). Get unlimited travel passes. Water taxis expensive. Walking and vaporetto only options.`,
          etiquette: `Venice has unique culture due to tourism pressure. Respect local residents. Don't sit on steps/bridges. Book restaurants advance.`,
          safety: `Very safe including at night. Emergency: Police 113, Carabinieri 112, Medical 118, EU 112. Main concern getting lost.`,
          food: `Breakfast: cappuccino with brioche. Seafood specialties - risotto al nero di seppia (squid ink). Cicchetti bars - Venetian tapas. Prosecco regional sparkling wine. Avoid tourist trap restaurants near St. Mark's.`
        },
        'dublin': {
          transport: `Download TFI Go app all public transport. Leap Card buses/trains/trams. Apps: Free Now, Uber, Hailo. Dublin Bikes citywide.`,
          etiquette: `Irish extremely friendly, chatty. Pub culture central - rounds system buying drinks. Tip 10-15%. Weather chat normal.`,
          safety: `Generally safe but careful Temple Bar late night. Emergency: 999 or 112. Gardaí (police) approachable, helpful.`,
          food: `Breakfast: full Irish breakfast hearty. Pub grub traditional. Guinness best in Ireland. Fish & chips popular. Irish stew classic. Soda bread traditional. Pub culture - buying rounds important social custom.`
        },
        
        // Asian Major Cities  
        'tokyo': {
          transport: `📱 **Essential Apps & Systems**
• Hyperdia (train schedules) - offline capability crucial
• Tokyo Metro/JR East apps - real-time delays, platform numbers
• Google Translate camera function - scan signs instantly
• IC Cards: Suica (JR) vs Pasmo (private railways) - both work everywhere

🚊 **Transport Hierarchy by Efficiency**
1. JR Yamanote Line (city loop) - every 2-4 mins
2. Tokyo Metro (9 lines) - connects major districts  
3. Private railways - Keio, Odakyu, Tokyu lines
4. Buses - backup option, more complex routing

💰 **Cost Analysis & Passes**
• JR Pass: ¥50,000 (7-day, online) / ¥53,000 via agency - only worth it for 3+ long-distance trips
• Tokyo Metro 24-hour Ticket: ¥700 vs ¥180-330 per ride
• Greater Tokyo Pass: ¥6,500 (5-day) - 13 private railways + Toei buses, no JR
• Rush hour surcharge avoided with flexible timing

⏰ **Strategic Timing**
• Avoid 7:30-9:30am, 5:30-7:30pm - 200% capacity
• Last trains 11:30pm-12:30am depending on line
• First trains 5:00am - early temple visits possible`,
          etiquette: `🙏 **Social Protocols & Hierarchy**
• Bowing depths: 15° (casual), 30° (business), 45° (formal apology)
• Business card ritual: Both hands, bow while receiving, never fold/write on
• Age hierarchy: Always defer to older person in groups
• Personal space: Avoid physical contact, phone calls on trains

🏠 **Home & Restaurant Customs**
• Shoe removal: Entrance genkan area, slippers provided indoor/outdoor
• Tatami rules: No shoes/slippers on straw mats ever
• Seating: Wait to be seated, cross-legged (men) vs sideways (women)
• Chopstick taboos: Never vertical in rice (funeral rite), no passing food chopstick-to-chopstick

💰 **Payment & Service Culture**  
• Tipping actively refused - implies service inadequacy
• Cash still king - many places don't accept cards
• Service levels: Omotenashi (hospitality) concept - perfection expected
• Complaints rare - quiet dissatisfaction more common

🗣️ **Communication Patterns**
• Indirect communication - read between lines, context matters
• "Hai" doesn't mean yes - means "I understand/am listening"
• Silence normal in conversation - don't fill gaps immediately
• Apologizing frequent - "sumimasen" used even when not at fault`,
          safety: `🚨 **Emergency Infrastructure**
• Police boxes (koban): Every few blocks, staffed 24/7, English assistance
• Emergency numbers: Police 110, Fire/Medical 119, Coast Guard 118
• Disaster preparedness: Earthquake early warning system on phones
• Tourist hotline: 050-3816-2787 (24/7 English support)

📊 **Crime Statistics & Reality**
• Violent crime rate: 0.3 per 1000 (vs US 3.7 per 1000)
• Pickpocketing almost non-existent - wallets left on tables safe
• Bicycle theft main concern - always lock, even convenience stores
• Scams rare but exist: Bar hostess overcharging in Kabukicho

🚇 **Transport Security Features**
• Platform doors prevent accidents - wait for complete stop
• Women-only cars available morning/evening rush hours
• Security cameras ubiquitous but privacy respected
• Lost items: 90%+ return rate through lost & found system

🌪️ **Natural Disaster Preparedness**
• Earthquake apps: Yurekuru, Safety tips (government app)
• Typhoon season: June-October, trains stop before dangerous winds
• Tsunami evacuation routes clearly marked coastal areas
• Emergency water/food caches in major buildings`,
          food: `🍱 **Meal Structure & Timing Analysis**
• Breakfast (7-9am): Traditional rice/miso/fish vs Western bread/coffee
• Lunch (11:30am-2pm): Set meals (teishoku) ¥500-1200, efficient service
• Dinner (6-9pm): Izakaya socializing vs family meals at home
• Late night: Convenience store quality exceptional, ramen until 2am

🏮 **Dining Categories & Price Points**
• Standing bars (tachinomi): ¥200-500 per drink/dish, quick turnover
• Chain restaurants: Yoshinoya, Sukiya - ¥400-600 full meals
• Mid-range: ¥2000-4000 per person, kaiseki starter level  
• High-end: ¥15,000+ omakase, months advance booking

🍜 **Regional Specialties & Etiquette**
• Ramen: Slurp loudly shows appreciation, finish everything quickly
• Sushi: Eat immediately when served, ginger palate cleanser only
• Tempura: Eat hot immediately, light dipping in tentsuyu sauce
• Wagyu grades: A5 highest (¥15,000+ per 100g), A4 excellent value

🥢 **Cultural Food Rules**
• Chopstick placement: Horizontal across bowl/plate when resting
• Sharing dishes: Use serving chopsticks (toribashi), not your own
• Alcohol: Never pour your own, always pour for others first
• Convenience stores: Microwave available, eating while walking discouraged

📊 **Seasonal Food Calendar**
• Spring (Mar-May): Sakura-mochi, bamboo shoots, fresh sea bream
• Summer (Jun-Aug): Cold noodles, shaved ice, eel for stamina
• Fall (Sep-Nov): Persimmons, pacific saury, matsutake mushrooms  
• Winter (Dec-Feb): Hot pot, osechi new year foods, winter illuminations food stalls`
        },
        'osaka': {
          transport: `Download Osaka Metro app, Hyperdia for JR trains. Osaka Metro day pass. Apps: Japan Taxi, DiDi. Rent bicycles short distances.`,
          etiquette: `Osakans more casual than Tokyo residents. Known for food culture - try everything! Stand right escalators. No tipping.`,
          safety: `Very safe, low crime rates. Emergency: Police 110, Fire/Medical 119. Shinsekai area gritty but not dangerous.`,
          food: `Food capital of Japan. Takoyaki (octopus balls) street food. Okonomiyaki savory pancakes. Kushikatsu fried skewers - don't double-dip! Kansai-style sushi different from Tokyo. Street food culture vibrant.`
        },
        'kyoto': {
          transport: `Download Kyoto City Bus app, Hyperdia trains. Kyoto City Bus Pass unlimited rides. Japan Taxi or traditional taxis. Rent bicycles temple hopping.`,
          etiquette: `Extra respect needed temples/traditional areas. Bow deeply, speak quietly. Don't photo geishas up close. Remove shoes temples.`,
          safety: `Extremely safe, respectful atmosphere. Emergency: Police 110, Fire/Medical 119. Temple areas well-patrolled.`,
          food: `Traditional kaiseki (multi-course) dining. Tofu cuisine Buddhist influence. Matcha tea ceremonies. Wagyu beef Kansai region. Traditional sweets (wagashi). Vegetarian options available temples.`
        },
        'seoul': {
          transport: `Download Subway Korea, Citymapper Seoul apps. T-money card all transport. Kakao Taxi app (Korean) or Uber. Seoul Bike sharing.`,
          etiquette: `Bow greeting elders. Remove shoes entering homes. Respect age hierarchy. Don't blow nose public. Tipping not expected.`,
          safety: `Very safe, low crime rates. Emergency: Police 112, Fire/Medical 119. Tourist helpline: 1330 (24/7 English). Women-only subway cars.`,
          food: `Kimchi at every meal. BBQ culture - cook at table. Banchan (side dishes) free. Soju national drink. Street food markets (Myeongdong). Spicy food common. Rice with most meals.`
        },
        'bangkok': {
          transport: `Download BTS SkyTrain, MRT apps. Use Grab cars/motorbikes. Negotiate tuk-tuk prices. Airport Rail Link. Avoid rush 7-9am, 5-8pm.`,
          etiquette: `Wai greeting (hands together, bow). Remove shoes temples/homes. Modest dress temples. King's image sacred.`,
          safety: `Generally safe, use common sense. Emergency: Tourist Police 1155, Police 191, Medical 1669. Gem/temple scams common.`,
          food: `Street food excellent and cheap. Pad Thai famous but try som tam (papaya salad). Very spicy - ask "mai pet" (not spicy). Fresh fruit abundant. Mango sticky rice dessert. Floating markets early morning.`
        },
        'singapore': {
          transport: `Download MyTransport app. EZ-Link card all transport. Apps: Grab, Gojek, ComfortDelGro. MRT very efficient.`,
          etiquette: `Multicultural respect. Importing/selling chewing gum is banned (chewing it is not). Don't jaywalk - fines up to S$1,000. Queue orderly. Right hand Muslims.`,
          safety: `One of world's safest. Emergency: Police 999, Fire/Medical 995. Strict laws, fair enforcement. No drugs (death penalty).`,
          food: `Hawker centers cheap, delicious - Hainanese chicken rice national dish. Chinese, Malay, Indian fusion. Chili crab specialty. Singapore sling cocktail. Strict food safety standards.`
        },
        'hong kong': {
          transport: `Download MTR Mobile, Citymapper apps. Octopus card all transport. Apps: Uber, traditional red taxis. Airport Express connects efficiently.`,
          etiquette: `Mix British-Chinese customs. Business cards both hands. Tip 10% restaurants. Don't discuss politics. Respect queueing.`,
          safety: `Very safe, excellent police force. Emergency: Police 999, Fire/Medical 999. Tourist police major areas speak English.`,
          food: `Dim sum brunch tradition. Char siu (BBQ pork) popular. Wonton noodles classic. Tea culture strong - yum cha. Egg tarts Portuguese influence. Milk tea Hong Kong style.`
        },
        'mumbai': {
          transport: `Download m-Indicator app local trains. Apps: Uber, Ola cars. Local trains extremely crowded. Auto-rickshaws short distances - negotiate price.`,
          etiquette: `Remove shoes homes/temples. Use right hand eating/greetings. Tip 10% restaurants. Dress modestly religious sites. Bargaining common markets.`,
          safety: `Generally safe but use street smarts. Emergency: Police 100, Fire 101, Medical 102. Watch belongings on trains.`,
          food: `Street food capital - vada pav (burger), bhel puri. South Indian dosa breakfast. Gujarati thali meals. Spicy food common. Vegetarian options abundant. Lassi yogurt drink.`
        },
        'delhi': {
          transport: `Download Delhi Metro Rail app, Uber/Ola cars. Metro excellent, air-conditioned. Auto-rickshaws negotiate price or use meter. Avoid buses rush hour.`,
          etiquette: `Delhi culture diverse. Remove shoes temples/homes. Use right hand eating. Tip 10% restaurants. Dress modestly religious sites.`,
          safety: `Requires caution especially women travelers. Emergency: Police 100, Fire 101, Medical 102, Women's Helpline 1091.`,
          food: `North Indian cuisine rich - butter chicken, naan bread. Paranthe Wali Gali famous bread street. Kebabs Mughlai influence. Lassi thick yogurt drink. Kulfi ice cream dessert.`
        },
        'bali': {
          transport: `Download Grab, Gojek apps. Rent scooter international license - cheapest. Blue Bird taxis reliable longer distances. Tourist shuttles between towns.`,
          etiquette: `Hindu majority - respect temple rules. Use right hand eating/greeting. Cover up temples. Negotiate prices markets. Don't point index finger.`,
          safety: `Safe but use common sense. Emergency: Police 110, Medical 118, Tourist Police 112. Watch motorbike theft. Travel insurance essential.`,
          food: `Nasi Goreng (fried rice) national dish. Rendang beef curry. Satay skewers popular. Tropical fruits exotic. Bintang beer local. Vegetarian options Hindu influence. Warung local eateries cheap.`
        },
        
        // North American Cities
        'new york': {
          transport: `Download MTA, Citymapper apps. MetroCard/OMNY contactless. Apps: Uber, Lyft. Citi Bike 1000+ stations. Subway 24/7.`,
          etiquette: `New Yorkers busy but helpful when asked. Walk fast, stand right. Tip 18-20% restaurants. Don't block subway doors.`,
          safety: `Safe but stay aware. Subway safer than reputation. Avoid empty cars late night. Emergency: 911.`,
          food: `Bagels and deli culture iconic. Pizza by the slice everywhere. Diverse food scene - every cuisine available. Diners open 24/7. Food trucks popular lunch option. Tipping 18-20% standard.`
        },
        'los angeles': {
          transport: `Download Metro LA app public transit. Car rental often necessary - traffic heavy. Apps: Uber, Lyft abundantly. Metro Rail connects some areas.`,
          etiquette: `Casual, relaxed culture. Health and fitness important. Tip 18-20% restaurants. Don't smoke most public areas. Beach culture laid back.`,
          safety: `Generally safe tourist areas. Emergency: 911. Some neighborhoods avoid at night. Beach areas well-patrolled during day.`,
          food: `Mexican cuisine excellent and authentic. In-N-Out burger local favorite. Korean BBQ in Koreatown. Health-conscious dining prevalent. Food truck culture strong. Farm-to-table restaurants popular.`
        },
        'toronto': {
          transport: `Download TTC app public transit. Presto card TTC subway/buses/streetcars. Apps: Uber, Lyft, Beck Taxi. Bike Share Toronto citywide.`,
          etiquette: `Canadians polite, say "sorry" frequently. Multiculturalism celebrated. Tip 15-20% restaurants. Remove shoes homes.`,
          safety: `Very safe, low crime rates. Emergency: 911. Tourist areas well-patrolled. Public transit safe late night.`,
          food: `Multicultural food scene - Little Italy, Chinatown, Greek Town. Poutine (fries with gravy/cheese curds) Canadian classic. Tim Hortons coffee culture. Peameal bacon sandwich Toronto specialty.`
        },
        'mexico city': {
          transport: `Download Moovit, Mi Transporte apps. Apps: Uber, DiDi, authorized taxis only. Metro efficient but crowded. Metrobús rapid transit major routes.`,
          etiquette: `Mexicans warm, family-oriented. Learn basic Spanish. "Por favor", "gracias" essential. Lunch main meal (2-4pm). Tip 10-15%.`,
          safety: `Requires street awareness but millions visit safely. Emergency: Police 911, Tourist Police 078. Use hotel recommendations. Drink bottled water.`,
          food: `Street food incredible - tacos al pastor, elote (corn). Lunch (comida) main meal 2-4pm. Mezcal and tequila tastings. Mercados (markets) authentic experience. Regional specialties vary by neighborhood.`
        },
        
        // Australian Cities
        'sydney': {
          transport: `Download TripView, Opal Travel apps. Opal card trains/buses/ferries. Apps: Uber, Ola, 13Cabs. Ferry to Manly scenic.`,
          etiquette: `Laid-back, friendly. "G'day mate" common. Tip 10% good service but not mandatory. Casual dress acceptable most places.`,
          safety: `Very safe, low crime. Emergency: 000. Swim between flags. Spider/snake awareness in bush.`,
          food: `Barbecue (BBQ) culture strong - "shrimp on the barbie". Flat white coffee originated here. Tim Tam biscuits national favorite. Meat pies popular snack. Fresh seafood excellent quality.`
        },
        'melbourne': {
          transport: `Download PTV Mobile app trams/trains/buses. myki card all transport. Apps: Uber, Ola, 13Cabs. Tram network extensive - free tram zone CBD.`,
          etiquette: `Strong coffee culture - ordering "latte" common. Arts and culture important. Tip 10% exceptional service. Dress more formally than Sydney.`,
          safety: `Very safe including late-night areas. Emergency: 000. Some outer suburbs require caution. Weather changes rapidly - carry layers.`,
          food: `Coffee capital of Australia - laneway café culture. Brunch culture strong. Greek food excellent in precinct. Food trucks and markets popular. Wine culture from nearby regions.`
        },
        
        // Middle Eastern Cities
        'dubai': {
          transport: `Download Dubai Metro, Careem apps. Nol card metro/buses/trams. Apps: Careem, Uber. Gold Class/Ladies cabins available.`,
          etiquette: `Conservative dress - cover shoulders/knees. No PDA. Friday holy day. Tip 10-15%. Remove shoes traditional areas.`,
          safety: `Extremely safe, zero crime tolerance. Emergency: Police 999, Fire/Medical 998. No drugs policy strict. Alcohol licensed venues only.`,
          food: `Middle Eastern cuisine excellent - hummus, shawarma, falafel. International dining scene in hotels. Alcohol only in licensed venues. Iftar buffets during Ramadan. Date fruits traditional.`
        },
        'istanbul': {
          transport: `Download Moovit, İBB Mobile apps. İstanbulkart all transport. Apps: Uber, BiTaksi, yellow taxis. Ferry Europe-Asia scenic.`,
          etiquette: `European-Asian mix. Remove shoes mosques/homes. Modest dress religious areas. Tea culture important. Bargain at bazaars.`,
          safety: `Generally safe, be aware surroundings. Emergency: Police 155, Fire 110, Medical 112. Tourist police major areas.`,
          food: `Turkish breakfast elaborate - cheese, olives, bread, honey. Kebab varieties authentic. Turkish tea (çay) social drink. Street food - döner, simit (Turkish bagel). Turkish delight traditional sweet.`
        },
        
        // South American Cities
        'buenos aires': {
          transport: `Download BA Cómo Llego, Moovit apps. SUBE card buses/metro. Apps: Uber, Cabify, radio taxis (avoid street taxis). Subway (Subte) covers central areas.`,
          etiquette: `Argentines warm, affectionate - cheek kisses greetings. Very late dinner (9-11pm). Tango culture important. Tip 10%. Don't discuss Falklands.`,
          safety: `Requires street awareness. Emergency: Police 911, Tourist Police 4346-5748. Use radio taxis, avoid La Boca at night.`,
          food: `Beef capital of world - asado (BBQ) culture. Empanadas popular snack. Mate tea social drink. Very late dinner 9-11pm. Wine culture excellent - Malbec specialty. Dulce de leche desserts.`
        },
        'rio de janeiro': {
          transport: `Download Moovit, Uber apps. RioCard buses/metro. Apps: Uber, yellow taxis with meters. Metro limited but connects major areas.`,
          etiquette: `Brazilians very friendly, physical - hugs/kisses normal. Beach culture relaxed. Learn basic Portuguese. Carnival has special customs. Tip 10%.`,
          safety: `Requires caution but millions visit safely. Emergency: Police 190, Tourist Police 21-2332-2924. Avoid favelas without guides.`,
          food: `Açaí bowls popular beach snack. Churrasco (Brazilian BBQ) all-you-can-eat. Caipirinha national cocktail. Feijoada black bean stew traditional. Fresh coconut water from street vendors.`
        },
        
        // African Cities
        'cape town': {
          transport: `Download Uber, Bolt apps - most reliable transport. MyCiTi bus connects some areas. Avoid minibus taxis as tourist. Car rental good wine routes.`,
          etiquette: `11 official languages, English widely spoken. Tip 10-15% expected. "Howzit" greeting. Respect cultural diversity.`,
          safety: `Requires vigilance but good tourism infrastructure. Emergency: Police 10111, Medical 10177. Township tours with guides only.`,
          food: `Wine culture world-class - Cape Winelands tours. Braai (BBQ) social tradition. Biltong dried meat snack. Bobotie traditional dish. Rooibos tea caffeine-free local favorite.`
        },
        'marrakech': {
          transport: `Download Careem app rides. Official taxis: beige petit (city), grands (longer). Negotiate prices advance. Walking medina often fastest.`,
          etiquette: `Muslim majority - respect Ramadan customs. Dress modestly especially medina. Bargaining essential souks. Small dirham tips common.`,
          safety: `Generally safe but alert in medina. Emergency: Police 19, Fire 15, Medical 15. Tourist police available. Use official guides only.`,
          food: `Tagine slow-cooked stews with vegetables/meat. Couscous traditional Friday meal. Mint tea (atay) social drink. Dates and nuts snacks. Pastilla sweet/savory pastry. Ramadan affects dining hours.`
        }
      };
      
      return destinationMap[baseDestination] || {
        transport: `In ${destination}, research local transport apps before arriving. Public transit is usually most economical. Ride-sharing apps often available in major cities. Negotiate taxi fares in advance. Consider bike rentals for short distances.`,
        etiquette: `Learn basic greetings in the local language - locals always appreciate the effort. Research tipping customs as they vary by country. Dress appropriately for religious sites. Be respectful of local customs and traditions.`,
        safety: `${destination} welcomes tourists but use common sense. Keep copies of important documents. Research local emergency numbers. Travel insurance recommended. Stay aware of your surroundings and trust your instincts.`
      };
    };
    
    const content = getTipsForDestination(destination, destinationCountry || 'Unknown');
    
    const baseTips: LocalTip[] = [
      {
        id: '1',
        category: 'transport',
        title: 'Public Transit & Getting Around',
        content: content.transport,
        author: 'Local Expert',
        rating: 4.9,
        likes: 127,
        estimatedCost: "Varies by city",
        icon: getCategoryIcon('transport'),
        lastUpdated: new Date(),
        verified: true,
        priority: 'high'
      },
      {
        id: '2',
        category: 'transport',
        title: 'Best Transportation Apps',
        content: `For ${destination}, download these essential apps: local transit app for real-time schedules, ride-sharing apps (Uber/local alternatives), parking apps if driving, and offline maps. Google Maps works globally but local apps often have better route optimization.`,
        author: 'Tech Traveler',
        rating: 4.8,
        likes: 95,
        estimatedCost: "Free downloads",
        icon: getCategoryIcon('transport'),
        lastUpdated: new Date(),
        verified: true,
        priority: 'high'
      },
      {
        id: '3',
        category: 'transport',
        title: 'Airport to City Transportation',
        content: `From ${destination} airport: Research express trains vs regular public transit - express often worth the extra cost for time savings. Pre-book airport transfers for guaranteed service. Avoid unofficial taxi touts. Many airports offer official bus services at fraction of taxi cost.`,
        author: 'Airport Pro',
        rating: 4.7,
        likes: 156,
        estimatedCost: "Varies",
        icon: getCategoryIcon('transport'),
        lastUpdated: new Date(),
        verified: true,
        priority: 'high'
      },
      {
        id: '7',
        category: 'etiquette',
        title: 'Cultural Etiquette & Manners',
        content: content.etiquette,
        author: 'Cultural Guide',
        rating: 4.9,
        likes: 156,
        icon: getCategoryIcon('etiquette'),
        lastUpdated: new Date(),
        verified: true,
        priority: 'high'
      },
      {
        id: '10',
        category: 'safety',
        title: 'Safety Tips & Emergency Info',
        content: content.safety,
        author: 'Safety Expert',
        rating: 4.8,
        likes: 203,
        estimatedCost: "Prevention is free",
        icon: getCategoryIcon('safety'),
        lastUpdated: new Date(),
        verified: true,
        priority: 'high'
      }
    ];

    return baseTips;
  };

  const handleUserTipSubmit = () => {
    if (userTip.title.trim() && userTip.content.trim()) {
      // Add user tip logic here
      setShowUserTipForm(false);
      setUserTip({ title: '', content: '', category: 'culture' });
    }
  };

  // Quick Travel Insights section
  const insightCategories: InsightCategory[] = [
    {
      id: 'safety',
      title: 'Safety & Health',
      icon: <Shield className="h-5 w-5" />,
      color: 'bg-[#F59E0B]', // Amber-yellow for safety
      description: 'Emergency contacts and health tips',
      priority: 1
    },
    {
      id: 'transportation',
      title: 'Transportation',
      icon: <Bus className="h-5 w-5" />,
      color: 'bg-[#06B6D4]', // Bright cyan-teal for transport
      description: 'Getting around like a local',
      priority: 2
    },
    {
      id: 'etiquette',
      title: 'Etiquette & Culture',
      icon: <Users className="h-5 w-5" />,
      color: 'bg-[#0D9488]', // Dark teal for culture
      description: 'Local customs, greetings, and social norms',
      priority: 3
    },
    {
      id: 'food',
      title: 'Food & Dining',
      icon: <Coffee className="h-5 w-5" />,
      color: 'bg-[#10B981]', // Green-teal for food
      description: 'Culinary experiences and dining etiquette',
      priority: 4
    }
  ];

  // Fetch live data only when destination is properly selected (minimum 3 characters) and user has access
  useEffect(() => {
    if (!insightDestination || insightDestination.length < 3 || !canUseLocalTips) {
      setLiveData(null);
      setLoadingLiveData(false);
      return;
    }

    // Reduced debounce since we have caching
    const timer = setTimeout(() => {
      fetchLiveInsights();
    }, 300);

    return () => clearTimeout(timer);
  }, [insightDestination, canUseLocalTips]);

  const fetchLiveInsights = async () => {
    if (!insightDestination) return;
      
      // Check cache first for instant loading
      const cached = getCachedData(insightDestination);
      if (cached) {
        console.log('Using cached data for', insightDestination);
        setLiveData(cached);
        setLoadingLiveData(false);
        return;
      }
      
      setLoadingLiveData(true);
      setDataError(null);
      
      // Use the stored country if available, otherwise detect it
      const detectCountry = (dest: string): string => {
        const dest_lower = dest.toLowerCase();
        const cityCountryMap: Record<string, string> = {
          'tokyo': 'Japan', 'osaka': 'Japan', 'kyoto': 'Japan',
          'paris': 'France', 'lyon': 'France', 'marseille': 'France',
          'rome': 'Italy', 'milan': 'Italy', 'florence': 'Italy', 'venice': 'Italy',
          'madrid': 'Spain', 'barcelona': 'Spain', 'seville': 'Spain',
          'london': 'United Kingdom', 'manchester': 'United Kingdom', 'edinburgh': 'United Kingdom',
          'berlin': 'Germany', 'munich': 'Germany', 'hamburg': 'Germany',
          'amsterdam': 'Netherlands', 'rotterdam': 'Netherlands',
          'vienna': 'Austria', 'salzburg': 'Austria',
          'zurich': 'Switzerland', 'geneva': 'Switzerland',
          'athens': 'Greece', 'thessaloniki': 'Greece', 'santorini': 'Greece',
          'istanbul': 'Turkey', 'dublin': 'Ireland',
          'lisbon': 'Portugal', 'porto': 'Portugal',
          'stockholm': 'Sweden', 'oslo': 'Norway', 'copenhagen': 'Denmark',
          'new york': 'United States', 'los angeles': 'United States',
          'toronto': 'Canada', 'vancouver': 'Canada',
          'sydney': 'Australia', 'melbourne': 'Australia',
          'bangkok': 'Thailand', 'singapore': 'Singapore',
          'dubai': 'United Arab Emirates'
        };
        return cityCountryMap[dest_lower] || 'Unknown';
      };

      const smartCountry = insightCountry || detectCountry(insightDestination);
      
      try {
        console.log(`Fetching live insights for ${insightDestination}, ${smartCountry}`);
        
        const { data, error } = await supabase.functions.invoke('destination-insights', {
          body: { 
            destination: insightDestination,
            country: smartCountry
          }
        });

        if (error) {
          console.error('Supabase function error:', error);
          throw error;
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        console.log('Live data received:', data);
        setLiveData(data);
        
        // Cache the successful response
        setCachedData(insightDestination, data);
        
      } catch (error) {
        console.error('Error fetching live insights:', error);
        setDataError(error instanceof Error ? error.message : 'Failed to fetch live data');
        
        // Fallback to static data
        const fallbackData = generateTravelTips(insightDestination, smartCountry);
        setLiveData({
          transport: fallbackData.transport,
          etiquette: fallbackData.etiquette,
          safety: fallbackData.safety,
          food: fallbackData.food
        });
        
        toast({
          title: "Using Cached Data",
          description: "Couldn't fetch live updates. Showing stored information.",
          variant: "default"
        });
      } finally {
        setLoadingLiveData(false);
      }
  };

  const handleInsightSearch = async () => {
    if (insightDestination.trim()) {
      setInsightLoading(true);
      try {
        setSelectedInsight('all');
      } catch (error) {
        console.error('Error initiating search:', error);
        toast({
          title: "Search Error",
          description: "Failed to start search. Please try again.",
          variant: "destructive"
        });
      } finally {
        setInsightLoading(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="local travel tips, destination guide, cultural insights, travel safety, local customs, transportation guide, travel etiquette, food culture, travel advice, tourist information" />
        <link rel="canonical" href={userDestination ? `https://odyvoy.com/local-tips/${userDestination.toLowerCase().replace(/\s+/g, '-')}` : 'https://odyvoy.com/local-tips'} />
        
        {/* Open Graph tags */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={userDestination ? `https://odyvoy.com/local-tips/${userDestination.toLowerCase().replace(/\s+/g, '-')}` : 'https://odyvoy.com/local-tips'} />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Structured Data - Travel Guide */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelGuide",
            "name": userDestination ? `${userDestination} Travel Guide` : "Local Travel Tips and Destination Guides",
            "description": pageDescription,
            "url": userDestination ? `https://odyvoy.com/local-tips/${userDestination.toLowerCase().replace(/\s+/g, '-')}` : 'https://odyvoy.com/local-tips',
            ...(userDestination && {
              "about": {
                "@type": "Place",
                "name": userDestination,
                "address": {
                  "@type": "PostalAddress",
                  "addressCountry": destinationCountry || ''
                }
              }
            }),
            "inLanguage": "en",
            "publisher": {
              "@type": "Organization",
              "name": "OdyVoy",
              "url": "https://odyvoy.com"
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Article",
                    "name": "Transportation & Getting Around",
                    "description": "Local transportation options, public transit tips, and getting around safely"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Article",
                    "name": "Cultural Etiquette & Local Customs",
                    "description": "Essential cultural norms, local etiquette, and respectful travel practices"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "item": {
                    "@type": "Article",
                    "name": "Safety & Security Tips",
                    "description": "Travel safety advice, emergency contacts, and security information"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "item": {
                    "@type": "Article",
                    "name": "Food & Dining Culture",
                    "description": "Local cuisine, dining customs, and food safety tips"
                  }
                }
              ]
            }
          })}
        </script>

        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What should I know before traveling to a new destination?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Research local customs and etiquette, transportation options, safety guidelines, and dining culture. Check visa requirements, vaccination needs, and travel advisories. Learn basic phrases in the local language and understand local currency and payment methods."
                }
              },
              {
                "@type": "Question",
                "name": "How do I find local transportation information?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Download local transit apps, research public transportation options, and check for tourist passes. Look into ride-sharing services, bike rentals, and walking routes. Always verify official taxi services and avoid unlicensed operators."
                }
              },
              {
                "@type": "Question",
                "name": "What are important cultural etiquette tips for travelers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Learn basic greetings and phrases, dress appropriately for the culture, respect local customs and traditions, understand tipping practices, be mindful of photography restrictions, and show respect in religious and cultural sites."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <section className="space-y-8" aria-labelledby="local-tips-heading" role="region">
        {/* Quick Travel Insights */}
        <Card className="border-cyan-500/20 overflow-hidden">
          <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-cyan-500/10">
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30">
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                </span>
                <h2 id="local-tips-heading" className="inline bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Quick Travel Insights
                </h2>
                {!canUseLocalTips && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Lock className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Available for Premium Economy & First Class Pack tiers</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Instant comprehensive travel guidance for destinations worldwide including local customs, transportation tips, dining etiquette, and safety information
              </CardDescription>
            </CardHeader>
          </div>
        <CardContent>
          <div className="space-y-3 sm:space-y-4 mb-6">
            <LocationAutocomplete
              placeholder="Search any destination..."
              value={insightDestination}
              onChange={setInsightDestination}
              onLocationSelect={(location) => {
                setInsightDestination(location.name);
                setInsightCountry(location.country);
                addRecentSearch(location.name, location.country);
              }}
              label=""
              className="h-12 sm:h-14 text-base sm:text-lg touch-manipulation"
              aria-label="Search destination for travel insights"
            />
            <Button 
              onClick={() => {
                if (!canUseLocalTips) {
                  toast({
                    title: "Premium Feature",
                    description: (
                      <div className="flex flex-col gap-2">
                        <p>Local Tips & Insights are available for Premium Economy Pack and First Class Pack tiers.</p>
                        <a href="/pricing" className="text-primary underline font-medium">Upgrade now to explore like a local!</a>
                      </div>
                    ),
                  });
                  return;
                }
                handleInsightSearch();
              }} 
              disabled={insightLoading} 
              className="w-full h-12 sm:h-14 text-base sm:text-lg touch-manipulation"
              aria-label="Explore destination insights"
            >
              {insightLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="h-5 w-5 mr-2" aria-hidden="true" />
                  Explore Like a Local
                </>
              )}
            </Button>
          </div>

          {/* Recently Searched Section */}
          {!selectedInsight && !insightDestination && (
            <div className="mb-6">
              <RecentSearches 
                onSelect={(dest, country) => {
                  setInsightDestination(dest);
                  setInsightCountry(country);
                  setSelectedInsight('all');
                  addRecentSearch(dest, country);
                }}
              />
            </div>
          )}


          {/* Popular Destinations Dropdown */}
          {!selectedInsight && !insightDestination && (
            <div className="mb-6">
              <Button
                variant="outline"
                onClick={() => setShowPopularDestinations(!showPopularDestinations)}
                className="w-full justify-between"
              >
                <span className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Popular Destinations
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showPopularDestinations ? 'rotate-180' : ''}`} />
              </Button>
              
              {showPopularDestinations && (
                <div className="mt-3 grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 animate-fade-in">
                  {POPULAR_DESTINATIONS.map((dest) => (
                    <Button
                      key={dest.name}
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setInsightDestination(dest.name);
                        setInsightCountry(dest.country);
                        setShowPopularDestinations(false);
                      }}
                      className="justify-start hover:bg-primary/10 h-auto py-3 px-3 touch-manipulation"
                    >
                      <span className="mr-2 text-xl sm:text-2xl">{dest.flag}</span>
                      <div className="text-left flex-1">
                        <div className="font-medium text-sm sm:text-base">{dest.name}</div>
                        <div className="text-xs text-muted-foreground">{dest.country}</div>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!selectedInsight && !insightDestination && (
            <div>
              <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                {insightCategories.map((category) => (
                  <Card 
                    key={category.id} 
                    className="cursor-pointer hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/30 hover:-translate-y-1 group"
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${category.title.toLowerCase()}`}
                  >
                    <CardContent className="p-4 text-center">
                      <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center text-white mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg`} aria-hidden="true">
                        {category.icon}
                      </div>
                      <h3 className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors">{category.title}</h3>
                      <p className="text-xs text-muted-foreground">{category.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Upgrade Prompt for Economy Tier Users */}
          {insightDestination && !canUseLocalTips && (
            <Card className="mt-4 animate-fade-in border-2 border-cyan-200 dark:border-teal-800">
              <CardContent className="p-8 text-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center">
                    <Crown className="h-8 w-8 text-white" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold">Unlock {insightDestination} Travel Insights</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Get comprehensive local tips, safety information, cultural etiquette, transportation guides, and dining recommendations for {insightDestination} and 6000+ destinations worldwide.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 mt-2">
                    <Button 
                      onClick={() => window.location.href = '/pricing'}
                      className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                    >
                      <Crown className="h-4 w-4 mr-2" />
                      Upgrade to Premium
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setInsightDestination('')}
                    >
                      Maybe Later
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Available for Premium Economy Pack & First Class Pack tiers
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Enhanced Destination Insights Display with Live Data */}
          {insightDestination && canUseLocalTips && loadingLiveData && (
            <Card className="mt-4 animate-fade-in">
              <CardContent className="p-8 space-y-6">
                {/* Loading skeleton for header */}
                <div className="space-y-3">
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                
                {/* Loading skeletons for categories */}
                <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2">
                  {[1, 2, 3, 4].map((i) => (
                    <Card key={i} className="overflow-hidden">
                      <div className="h-2 bg-muted animate-pulse"></div>
                      <CardHeader className="pb-3 sm:pb-4">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl" />
                          <Skeleton className="h-5 sm:h-6 w-32 sm:w-40" />
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2 sm:space-y-3">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-5/6" />
                        <Skeleton className="h-4 w-4/6" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="text-center">
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                    <Globe className="h-4 w-4 animate-spin" />
                    Fetching live travel insights for {insightDestination}...
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          

          {insightDestination && canUseLocalTips && !loadingLiveData && liveData && (() => {

            const categories = [
              { 
                id: 'safety', 
                title: 'Safety & Emergency Info', 
                icon: <Shield className="h-6 w-6" />, 
                gradient: 'from-cyan-500 to-cyan-500',
                bgGradient: 'from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30',
                content: liveData.safety 
              },
              { 
                id: 'transport', 
                title: 'Transport & Getting Around', 
                icon: <Bus className="h-6 w-6" />, 
                gradient: 'from-cyan-500 to-cyan-500',
                bgGradient: 'from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30',
                content: liveData.transport 
              },
              { 
                id: 'etiquette', 
                title: 'Local Etiquette & Culture', 
                icon: <Users className="h-6 w-6" />, 
                gradient: 'from-cyan-500 to-cyan-500',
                bgGradient: 'from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30',
                content: liveData.etiquette 
              },
              { 
                id: 'food', 
                title: 'Food Culture & Dining', 
                icon: <Coffee className="h-6 w-6" />, 
                gradient: 'from-cyan-500 to-cyan-500',
                bgGradient: 'from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30',
                content: liveData.food 
              }
            ];

            return (
              <article className="animate-fade-in border-2 border-cyan-500/20 rounded-xl overflow-hidden shadow-sm shadow-cyan-500/10" itemScope itemType="https://schema.org/TravelGuide">
                <Card className="border-0">
                <meta itemProp="name" content={`${insightDestination} Travel Guide`} />
                <meta itemProp="description" content={`Comprehensive travel information for ${insightDestination} including local customs, transportation, safety, and dining etiquette`} />
                <CardHeader className="pb-3 sm:pb-4 bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-cyan-500/10 rounded-t-lg relative">
                  <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
                  <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                    <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white shadow-md shadow-cyan-500/30 flex-shrink-0" aria-hidden="true">
                        <MapPin className="h-5 w-5 sm:h-6 sm:w-6" />
                      </div>
                      <h3 itemProp="about" className="break-words bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">{insightDestination.charAt(0).toUpperCase() + insightDestination.slice(1)} Travel Guide</h3>
                    </CardTitle>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      {subscription.subscription_tier === 'first_class' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={handleDownloadPDF}
                          className="hover:bg-primary/10 h-10 w-10 sm:h-9 sm:w-9 p-0 touch-manipulation"
                          aria-label="Download PDF for offline access"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="hover:bg-primary/10 h-10 w-10 sm:h-9 sm:w-9 p-0 touch-manipulation"
                        aria-label="Refresh data"
                      >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowFilters(!showFilters)}
                        className="hover:bg-primary/10"
                        aria-label="Toggle filters"
                      >
                        <Filter className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setInsightDestination('')}
                        className="hover:bg-destructive/10 hover:text-destructive"
                        aria-label="Close"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardDescription className="text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-500" aria-hidden="true" />
                      <span itemProp="description">Live, current information for your trip</span>
                    </CardDescription>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" aria-hidden="true" />
                      <time dateTime={lastRefreshed.toISOString()}>
                        Last updated: {lastRefreshed.toLocaleTimeString()}
                      </time>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 sm:p-6">
                  {/* Mobile-first responsive grid */}
                  <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0" role="list" aria-label="Travel information categories">
                    {categories.map((category, index) => (
                      <article key={category.id} className={`overflow-hidden border-2 transition-all duration-300 animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }} itemScope itemType="https://schema.org/HowTo" role="listitem">
                        <Card className="border-0">
                        <meta itemProp="name" content={category.title} />
                        {/* Category header - mobile optimized */}
                        <div className={`h-2 bg-gradient-to-r ${category.gradient}`} role="presentation"></div>
                        <CardHeader className="pb-4 px-4 sm:px-6">
                          <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r ${category.gradient} flex items-center justify-center text-white shadow-lg`} aria-hidden="true">
                              {category.icon}
                            </div>
                            <h4 className="font-bold" itemProp="name">{category.title}</h4>
                          </CardTitle>
                        </CardHeader>
                        
                        {/* Mobile-optimized content */}
                        <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                          <div className={`rounded-xl bg-gradient-to-r ${category.bgGradient} border-2 overflow-hidden`}>
                            {(() => {
                              const lines = category.content.split('\n');
                              const sections: { header: string; content: string[] }[] = [];
                              let currentSection: { header: string; content: string[] } | null = null;

                              lines.forEach((line) => {
                                if (line.match(/^[🚊📱💰⏰🙏🏠🗣️🚨📊🚇🌪️🍱🏮🍜🥢🧀🥐🍷⛪🏛️🇬🇷🇮🇹🇫🇷🥖🍝🎨🫒🐟🍇]/)) {
                                  if (currentSection) {
                                    sections.push(currentSection);
                                  }
                                  currentSection = { header: line, content: [] };
                                } else if (currentSection && line.trim() !== '') {
                                  currentSection.content.push(line);
                                }
                              });

                              if (currentSection) {
                                sections.push(currentSection);
                              }

                              return (
                                <div className="max-h-96 overflow-y-auto" role="list" aria-label={`${category.title} information sections`}>
                                  {sections.map((section, sectionIdx) => (
                                    <Collapsible key={sectionIdx} className="border-b border-white/10 last:border-b-0" role="listitem">
                                      {/* Mobile-friendly trigger with larger tap target */}
                                      <CollapsibleTrigger 
                                        className="flex items-center justify-between w-full p-4 sm:p-5 hover:bg-white/10 active:bg-white/15 transition-colors group min-h-[60px] touch-manipulation"
                                        aria-label={`Toggle ${section.header} information`}
                                      >
                                        <h5 className="font-semibold text-sm sm:text-base flex items-center gap-2 text-left flex-1 pr-2" itemProp="step">
                                          {section.header}
                                        </h5>
                                        <ChevronDown className="h-5 w-5 sm:h-4 sm:w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180 opacity-70" aria-hidden="true" />
                                      </CollapsibleTrigger>
                                      
                                       {/* Mobile-optimized content */}
                                       <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                                          <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0" itemProp="text">
                                             <div className="space-y-2 sm:space-y-3" role="list" aria-label={`${section.header} details`}>
                                              {section.content.map((line, lineIdx) => {
                                                // Check for emergency numbers (phone numbers pattern)
                                                const phoneMatch = line.match(/(\d{3}|\d{2,3}[-\s]?\d{3,4}[-\s]?\d{3,4})/);
                                                const isEmergency = section.header.includes('🚨') && phoneMatch;
                                                const isSafetySection = category.id === 'safety';
                                                
                                                // Determine tip type for safety section
                                                let tipType: 'emergency' | 'warning' | 'info' = 'info';
                                                if (isSafetySection) {
                                                  if (phoneMatch || line.toLowerCase().includes('emergency') || line.toLowerCase().includes('police') || line.toLowerCase().includes('ambulance') || line.toLowerCase().includes('fire')) {
                                                    tipType = 'emergency';
                                                  } else if (line.toLowerCase().includes('warning') || line.toLowerCase().includes('avoid') || line.toLowerCase().includes('caution') || line.toLowerCase().includes('beware') || line.toLowerCase().includes('watch')) {
                                                    tipType = 'warning';
                                                  }
                                                }
                                                
                                                 if (line.startsWith('• ') || line.startsWith('- ')) {
                                                   const content = line.substring(2);
                                                   return (
                                                     <div 
                                                       key={lineIdx} 
                                                       role="listitem"
                                                       aria-label={`${tipType === 'emergency' ? 'Emergency information' : tipType === 'warning' ? 'Safety warning' : 'Travel tip'}: ${content}`}
                                                       className={`flex items-start gap-2 sm:gap-3 p-3 rounded-lg transition-all ${
                                                         isSafetySection && tipType === 'emergency' 
                                                           ? 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50' 
                                                           : isSafetySection && tipType === 'warning'
                                                           ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50'
                                                           : ''
                                                       }`}
                                                     >
                                                      {isSafetySection && (
                                                        <div className="flex-shrink-0 mt-0.5">
                                                          {tipType === 'emergency' ? (
                                                            <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                                                              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                                            </div>
                                                          ) : tipType === 'warning' ? (
                                                            <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                                                              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                                            </div>
                                                          ) : (
                                                            <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                                                              <Info className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                      {!isSafetySection && (
                                                        <div className="w-1.5 h-1.5 rounded-full bg-current mt-2 flex-shrink-0"></div>
                                                      )}
                                                       <div className="flex-1 flex items-start gap-2 flex-col sm:flex-row sm:items-center sm:justify-between">
                                                         <span className={`text-sm sm:text-base leading-relaxed w-full sm:w-auto text-left ${
                                                           isSafetySection && tipType === 'emergency' ? 'font-medium text-red-900 dark:text-red-100' : ''
                                                         }`}>
                                                           {content}
                                                         </span>
                                                        {isEmergency && phoneMatch && (
                                                          <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
                                                            <Button
                                                              size="sm"
                                                              variant="default"
                                                              className="flex-1 sm:flex-none h-9 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white"
                                                              onClick={() => makePhoneCall(phoneMatch[0])}
                                                              aria-label={`Call ${phoneMatch[0]}`}
                                                            >
                                                              <Phone className="h-4 w-4 mr-1.5" />
                                                              Call
                                                            </Button>
                                                            <Button
                                                              size="sm"
                                                              variant="outline"
                                                              className="flex-1 sm:flex-none h-9 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/50"
                                                              onClick={() => copyToClipboard(phoneMatch[0], 'Emergency number')}
                                                              aria-label={`Copy ${phoneMatch[0]}`}
                                                            >
                                                              <Copy className="h-4 w-4 mr-1.5" />
                                                              Copy
                                                            </Button>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                }
                                                return (
                                                  <p key={lineIdx} className="text-sm sm:text-base leading-relaxed mb-2 pl-2">
                                                    {line}
                                                  </p>
                                                );
                                              })}
                                            </div>
                                         </div>
                                       </CollapsibleContent>
                                     </Collapsible>
                                  ))}
                                  
                                  {/* Mobile helper text */}
                                  <div className="p-3 text-center border-t border-white/10 bg-white/5">
                                    <p className="text-xs opacity-70">Tap sections above to expand</p>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                         </CardContent>
                       </Card>
                       </article>
                     ))}
                   </div>
                 </CardContent>
               </Card>
               </article>
             );
           })()}
        
        </CardContent>
      </Card>

      {/* eSIM Recommendations */}
      <ESIMRecommendations />

    </section>
    </>
  );
}